'use client';

import { motion } from 'framer-motion';
import { useState } from 'react';

export default function ContactForm() {
  const [status, setStatus] = useState<
    'idle' | 'inProgress' | 'success' | 'failed'
  >('idle');
  const [statusMessage, setStatusMessage] = useState<string>('');
  const [showAlert, setShowAlert] = useState<boolean>(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('inProgress');
    const formData = new FormData(e.target as HTMLFormElement);

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.get('name'),
          email: formData.get('email'),
          phone: formData.get('phone'),
          message: formData.get('message'),
        }),
      });

      const result = await response.json();
      setStatus(result.status as 'idle' | 'inProgress' | 'success' | 'failed');
      setStatusMessage(result.message);
      setShowAlert(true);

      if (response.ok) {
        setTimeout(() => {
          setShowAlert(false);
          (e.target as HTMLFormElement).reset();
        }, 5000);
      }
    } catch (error) {
      console.error('Error in API request:', error);
      setStatus('failed');
      setStatusMessage('Oops! Something went wrong. Please try again later.');
      setShowAlert(true);
    }
  };

  return (
    <div className="min-h-screen bg-base-100">
      <div className="py-8 text-center text-base-content">
        <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-bold">
          Contact Us
        </h1>
      </div>

      <section className="section">
        <div className="card container mx-auto">
          <div className="contact-container">
            <div className="card p-16 bg-base-200 mx-8">
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
              >
                <form
                  onSubmit={handleSubmit}
                  className="contact-us-form w-full space-y-6"
                  aria-labelledby="contact-form-title"
                >
                  <h2 id="contact-form-title" className="sr-only">
                    Contact Form
                  </h2>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="contact-field-wrap">
                      <label htmlFor="name" className="label">
                        <span className="label-text">Full Name</span>
                        <span className="label-text-alt text-primary">*</span>
                      </label>
                      <input
                        id="name"
                        type="text"
                        name="name"
                        className="input w-full"
                        placeholder="John Doe"
                        required
                      />
                    </div>
                    <div className="contact-field-wrap">
                      <label htmlFor="email" className="label">
                        <span className="label-text">Email</span>
                        <span className="label-text-alt text-primary">*</span>
                      </label>
                      <input
                        id="email"
                        type="email"
                        name="email"
                        className="input w-full"
                        placeholder="Enter your email"
                        required
                      />
                    </div>
                  </div>

                  <div className="contact-field-wrap">
                    <label htmlFor="phone" className="label">
                      <span className="label-text">Phone Number</span>
                    </label>
                    <input
                      id="phone"
                      type="tel"
                      name="phone"
                      className="input w-full"
                      placeholder="+1 234 5678"
                    />
                  </div>

                  <div className="contact-field-wrap">
                    <label htmlFor="message" className="label">
                      <span className="label-text">Message</span>
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      className="w-full textarea"
                      placeholder="Leave your message"
                    ></textarea>
                  </div>

                  <div className="flex justify-end">
                    <motion.button
                      type="submit"
                      className="btn-primary btn"
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.95 }}
                      disabled={status === 'inProgress'}
                    >
                      {status === 'inProgress' ? (
                        <span className="loading loading-spinner loading-md"></span>
                      ) : (
                        'Submit'
                      )}
                    </motion.button>
                  </div>

                  {showAlert && status === 'success' && (
                    <div role="alert" className="alert alert-success mt-4">
                      <svg
                        className="h-6 w-6 shrink-0 stroke-current"
                        viewBox="0 0 24 24"
                        fill="none"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      <span>{statusMessage}</span>
                    </div>
                  )}

                  {showAlert && status === 'failed' && (
                    <div role="alert" className="alert alert-error mt-4">
                      <svg
                        className="h-6 w-6 shrink-0 stroke-current"
                        viewBox="0 0 24 24"
                        fill="none"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      <span>{statusMessage}</span>
                    </div>
                  )}
                </form>
              </motion.div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
